import React, { type ReactNode } from "react";
import { type CaseStudySimple } from "lib/sanity";
import { AspectRatio, Box, Flex, Text } from "@chakra-ui/react";
import { Button } from "ui/button";
import { useSwiperSlide } from "swiper/react";
import Balancer from "react-wrap-balancer";
import Image from "next/image";

export type SliderCardProps = {
  /** The case study data for the card. Provides case study name and logo. */
  caseStudy: CaseStudySimple;
  /** The title for the card. */
  title: string;
  /** The media to display on the card, typically either a video or an image. */
  media: ReactNode;
  /** The url for the card to link to. */
  url: string;
};

export function SliderCard({ caseStudy, title, media, url }: SliderCardProps) {
  const swiperSlide = useSwiperSlide();
  const isActive = swiperSlide?.isActive;

  return (
    <Flex
      cursor={isActive ? "default" : "grab"}
      flexDir="column"
      gap={{ base: "small", md: "bigger" }}
      w={{ base: "280px", sm: "440px", md: "570px", lg: "800px" }}
      opacity={isActive ? 1 : 0.6}
      transition="opacity 0.15s ease-in-out"
    >
      <AspectRatio ratio={16 / 9} borderRadius={{ base: "small", md: "big" }} w="100%" maxW="870px" overflow="hidden">
        <Flex pos="relative" boxSize="100%">
          {media}
          <Logo caseStudy={caseStudy} />
        </Flex>
      </AspectRatio>

      <Flex
        align={{ base: "center", md: "center" }}
        flexDir={{ base: "column", md: "row" }}
        justify="space-between"
        gap="small"
        px={{ base: "smallest", sm: "small", md: "bigger" }}
      >
        <Text
          as="h2"
          textStyle="headline3"
          fontSize={{ base: "brand.md", md: "brand.lg" }}
          textAlign={{ base: "center", md: "left" }}
        >
          <Balancer>{title}</Balancer>
        </Text>
        <Button hasRightChevron href={url} flexShrink={0} target={url.includes("http") ? "_blank" : "_self"}>
          Read story
        </Button>
      </Flex>
    </Flex>
  );
}

function Logo({ caseStudy }: { caseStudy: CaseStudySimple }) {
  return (
    <>
      <Box pos="absolute" inset={0} bgGradient="linear(165deg, rgba(0,0,0,0.4), transparent 50%)" />
      <Box
        pos="absolute"
        top={{ base: "smallest", sm: "smaller", lg: "38px" }}
        left={{ base: "smaller", sm: "small", lg: "48px" }}
      >
        <AspectRatio pos="relative" ratio={160 / 60} width={{ base: "80px", sm: "100px", md: "160px" }}>
          <Image
            width={160}
            height={60}
            src={caseStudy.customer.logoMonoUrl ?? caseStudy.customer.logoUrl}
            alt={`${caseStudy.customer.name} logo.`}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              objectPosition: "left",
              filter:
                "brightness(0) invert(1) drop-shadow(0px 0px 15px rgba(0, 0, 0, 0.4)) drop-shadow(0px 0px 20px rgba(0, 0, 0, 0.1)) drop-shadow(0px 0px 40px rgba(0, 0, 0, 0.1))",
            }}
          />
        </AspectRatio>
      </Box>
    </>
  );
}
